import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Settings, Type, Text as TextIcon, Grip, Sidebar } from 'lucide-react';
import { useUISettings } from './UISettingsContext';

const TextScaleOption = ({ value, label, active, onSelect }) => (
  <button
    onClick={() => onSelect(value)}
    className={`px-3 py-1 rounded border text-sm transition-colors ${
      active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
    }`}
  >
    {label}
  </button>
);

const ModeOption = ({ value, label, active, onSelect }) => (
  <button
    onClick={() => onSelect(value)}
    className={`px-3 py-1 rounded border text-sm transition-colors ${
      active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
    }`}
  >
    {label}
  </button>
);

const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

const PopupSetting = () => {
  const { textScale, setTextScale, tabLabelMode, setTabLabelMode, popupPosition, setPopupPosition } = useUISettings();
  const [open, setOpen] = useState(true);
  const dragRef = useRef(null);
  const posRef = useRef(popupPosition);

  useEffect(() => {
    posRef.current = popupPosition;
  }, [popupPosition]);

  const onPointerDown = useCallback((e) => {
    const startX = e.clientX || (e.touches && e.touches[0]?.clientX);
    const startY = e.clientY || (e.touches && e.touches[0]?.clientY);
    if (startX == null || startY == null) return;
    const startPos = posRef.current;

    const move = (ev) => {
      const clientX = ev.clientX || (ev.touches && ev.touches[0]?.clientX);
      const clientY = ev.clientY || (ev.touches && ev.touches[0]?.clientY);
      if (clientX == null || clientY == null) return;

      const dx = clientX - startX;
      const dy = clientY - startY;
      const next = { x: startPos.x + dx, y: startPos.y + dy };
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const width = open ? 300 : 40;
      const height = open ? 180 : 40;
      setPopupPosition({
        x: clamp(next.x, 8, vw - width - 8),
        y: clamp(next.y, 8, vh - height - 8),
      });
    };

    const up = () => {
      window.removeEventListener('pointermove', move, { passive: true });
      window.removeEventListener('pointerup', up, { passive: true });
      window.removeEventListener('touchmove', move, { passive: true });
      window.removeEventListener('touchend', up, { passive: true });
      posRef.current = popupPosition;
    };

    window.addEventListener('pointermove', move, { passive: true });
    window.addEventListener('pointerup', up, { passive: true });
    window.addEventListener('touchmove', move, { passive: true });
    window.addEventListener('touchend', up, { passive: true });
  }, [popupPosition, setPopupPosition, open]);

  return (
    <div className="fixed z-[100]" style={{ left: popupPosition.x, top: popupPosition.y }}>
      {open ? (
        <div className="rounded-lg shadow-lg border border-gray-200 w-[300px] bg-white select-none">
          <div
            ref={dragRef}
            onPointerDown={onPointerDown}
            onTouchStart={onPointerDown}
            className="flex items-center justify-between px-3 py-2 bg-gray-100 rounded-t-lg cursor-grab active:cursor-grabbing"
          >
            <div className="flex items-center gap-2 text-gray-700">
              <Grip className="w-4 h-4" />
              <Settings className="w-4 h-4" />
              <span className="text-sm font-medium">Popup Setting</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-xs text-gray-600 hover:text-gray-800"
              aria-label="Hide settings"
            >
              Hide
            </button>
          </div>

          <div className="p-3 space-y-4">
            <div>
              <div className="flex items-center gap-2 text-gray-700 mb-2">
                <Type className="w-4 h-4" />
                <span className="text-sm font-medium">Text size</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <TextScaleOption value="large" label="Large" active={textScale === 'large'} onSelect={setTextScale} />
                <TextScaleOption value="medium" label="Medium" active={textScale === 'medium'} onSelect={setTextScale} />
                <TextScaleOption value="small" label="Small" active={textScale === 'small'} onSelect={setTextScale} />
                <TextScaleOption value="tiny" label="Tiny" active={textScale === 'tiny'} onSelect={setTextScale} />
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 text-gray-700 mb-2">
                <Sidebar className="w-4 h-4" />
                <span className="text-sm font-medium">Tab labels</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <ModeOption value="icon-only" label="Tab icon only" active={tabLabelMode === 'icon-only'} onSelect={setTabLabelMode} />
                <ModeOption value="icon-text" label="Tab icon + text" active={tabLabelMode === 'icon-text'} onSelect={setTabLabelMode} />
              </div>
              <div className="text-xs text-gray-500 mt-1">Affects main tabs and Grammar categories.</div>
            </div>
          </div>
        </div>
      ) : (
        <button
          ref={dragRef}
          onPointerDown={onPointerDown}
          onTouchStart={onPointerDown}
          onClick={() => setOpen(true)}
          aria-label="Show settings"
          className="w-10 h-10 rounded-full shadow-lg border border-gray-200 bg-white flex items-center justify-center select-none cursor-grab active:cursor-grabbing"
        >
          <span className="font-semibold text-gray-700">T</span>
        </button>
      )}
    </div>
  );
};

export default PopupSetting;


